import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalVariablesContext } from '../../context/GlobalVariablesContext';
import { usePermission } from '../../hooks/usePermission';
import { canManageIncidence, canCreateIncidence, deleteIncidence } from '../../services/incidenceService';
import { getVisorID } from '../../utils/misc';
import MapContext from '../Map/MapContext';

import { formatDate, isValidDate, refreshLayerByName } from './../helpers/index';

import TabNavigation from '../Bootstrap/Tabs/TabNavigation';
import TabContent from '../Bootstrap/Tabs/TabContent';
import ImageGallery from '../ImageGallery/ImageGallery';
import ReviewHistory from '../IncidenceReview/ReviewHistory';
import ReviewForm from '../IncidenceReview/ReviewForm';

/**
 * Componente que muestra los atributos de un feature en formato de ficha.
 * - Primero, intenta obtener los atributos desde `config` (LAYERS o GROUPS).
 * - Si no hay configuración para el feature, intenta filtrar usando `record_information_default_attributes`.
 * - Si no hay atributos definidos en `config` ni `record_information_default_attributes`, muestra todas las propiedades.
 */
const CardFeature = ({ feature, onStartGeometryEdit, onCloseModal }) => {
    const { config } = useGlobalVariablesContext();
    const { t } = useTranslation();
    const { map } = useContext(MapContext);

    const [attachments, setAttachments] = useState([]);
    const [activeTab, setActiveTab] = useState('details');
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewKey, setReviewKey] = useState(0);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const featureProps = feature?.feature || feature || {};
    const isIncidence = Boolean(featureProps.map_viewer_id && featureProps.status !== undefined && featureProps.id);
    const incidenceId = isIncidence ? featureProps.id : null;

    useEffect(() => {
        if (isIncidence) {
            setCurrentStatus(featureProps.status);
        }
    }, [isIncidence, featureProps.status]);

    const mapViewerId = getVisorID();
    const { allowed: canManage } = usePermission(
        isIncidence && mapViewerId ? 'incidence.manage' : null,
        (signal) => canManageIncidence(mapViewerId, { signal })
    );

    const { allowed: canCreate } = usePermission(
        isIncidence && mapViewerId ? 'incidence.create' : null,
        (signal) => canCreateIncidence(mapViewerId, { signal })
    );

    const handleReviewCreated = (updatedIncidence) => {
        setShowReviewForm(false);
        setReviewKey((prev) => prev + 1);
        if (updatedIncidence?.status) {
            setCurrentStatus(updatedIncidence.status);
        }
        refreshLayerByName(map, 'incidencias_pendientes');
        refreshLayerByName(map, 'incidencias_revisadas');
        refreshLayerByName(map, 'incidencias_cerradas');
    };

    const handleDelete = async () => {
        if (!incidenceId || !window.confirm(t('¿Estás seguro de que deseas eliminar esta incidencia?'))) return;

        setIsDeleting(true);
        try {
            const res = await deleteIncidence(incidenceId);
            if (res.ok) {
                refreshLayerByName(map, 'incidencias_pendientes');
                refreshLayerByName(map, 'incidencias_revisadas');
                refreshLayerByName(map, 'incidencias_cerradas');
                if (onCloseModal) onCloseModal();
            }
        } catch (error) {
            console.error('Error deleting incidence:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    // MAPEO: Funciones de transformación para atributos específicos
    const transformationFunctions = {
        validated: (value) => value ? t("Yes") : t("No"),
    };

    /**
     * Detecta si un campo es de tipo fecha por su nombre
     * Campos que terminan en _at, _date, o contienen 'date'
     */
    const isDateField = (fieldName) => {
        const datePatterns = /(_at|_date|date)/i;
        return datePatterns.test(fieldName);
    };

    /**
     * Transforma un valor dinámicamente según su tipo
     */
    const transformValue = (key, value, properties) => {
        // Si el valor es un objeto, convertirlo a string
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            // Para objetos, intentar stringify (manejando referencias circulares)
            try {
                return JSON.stringify(value);
            } catch (error) {
                // Si hay referencias circulares u otro error, devolver indicador
                console.error('Error al stringify el objeto:', error);
                return '[Complex Object]';
            }
        }

        // Primero aplicar transformaciones específicas si existen
        if (transformationFunctions[key]) {
            return transformationFunctions[key](value, properties);
        }

        // Convertir a string para validar fechas
        const stringValue = String(value);

        // Si es un campo de fecha, formatearlo
        if (isDateField(key)) {
            if (isValidDate(stringValue)) {
                return formatDate(stringValue);
            }
            // Si no pasa la validación pero parece fecha, intentar formatear
            if (stringValue.match(/^\d{4}-\d{2}-\d{2}/)) {
                return formatDate(stringValue);
            }
        }

        // Si el valor es una fecha válida (por contenido), formatearlo
        if (isValidDate(stringValue)) {
            return formatDate(stringValue);
        }

        return value;
    };


    if (!config || !feature) {
        console.warn("Config o feature no están definidos");
        return null;
    }

    let filteredProperties = [];
    const featureProperties = feature.feature || feature;

    // OBTENER LOS ATRIBUTOS DE UNA CAPA
    if (feature.id_ === undefined) {
        const incidenceAttrsConfig = config?.incidence_attributes;
        const incidenceAttrsList = isIncidence && incidenceAttrsConfig
            ? (canManage ? incidenceAttrsConfig.manage : incidenceAttrsConfig.view)
            : null;

        if (isIncidence && incidenceAttrsList && Array.isArray(incidenceAttrsList)) {
            filteredProperties = incidenceAttrsList
                .filter(key => key in featureProperties)
                .map(key => {
                    let value = featureProperties[key];
                    if (key === 'status' && currentStatus) {
                        value = currentStatus;
                    }
                    const transformedValue = transformValue(key, value, featureProperties);
                    return [key, transformedValue];
                });
        } else {
            // Priority 2: per-layer fieldConfig
            const layerName = featureProperties._olLayer?.get?.('name');
            const allConfigLayers = [
                ...(config?.LAYERS || []),
                ...(config?.GROUPS || []).flatMap(g => g.layers || []),
            ];
            const layerConfig = layerName
                ? allConfigLayers.find(l => l.name === layerName)
                : null;
            const layerFieldConfig = layerConfig?.fieldConfig;

            if (layerFieldConfig && Array.isArray(layerFieldConfig) && layerFieldConfig.length > 0) {
                filteredProperties = layerFieldConfig
                    .filter(fc => fc.visible !== false && fc.name in featureProperties)
                    .map(fc => {
                        const value = featureProperties[fc.name];
                        const transformedValue = transformValue(fc.name, value, featureProperties);
                        return [fc.alias?.trim() || fc.name, transformedValue, fc.type];
                    });
            } else {
                // Priority 3: global default attributes
                const properties_default = config?.record_information_default_attributes;
                const hasPropertiesDefault = properties_default && Array.isArray(properties_default) && properties_default.length > 0;

                if (hasPropertiesDefault) {
                    filteredProperties = properties_default
                        .filter(key => key in featureProperties && key !== "_olFeature" && key !== "_olLayer" && key !== "files")
                        .map(key => {
                            const value = featureProperties[key];
                            const transformedValue = transformValue(key, value, featureProperties);
                            return [key, transformedValue];
                        });
                } else {
                    // Priority 4: all feature properties
                    filteredProperties = Object.entries(featureProperties)
                        .filter(([key]) => key !== "_olFeature" && key !== "_olLayer" && key !== "files")
                        .map(([key, value]) => {
                            const transformedValue = transformValue(key, value, featureProperties);
                            return [key, transformedValue];
                        });
                }

                if (filteredProperties.length === 0) {
                    filteredProperties = Object.entries(featureProperties);
                }
            }
        }
    }

    if (!filteredProperties.length) {
        console.warn("No hay propiedades filtradas para mostrar.");
        return <p>{t("No data available")}</p>;
    }

    // Extraer adjuntos si existen
    useEffect(() => {
        const adjuntos = filteredProperties.find(([key]) => key === "files");
        if (adjuntos && Array.isArray(adjuntos[1]) && adjuntos[1].length > 0) {
            setAttachments(adjuntos[1]);
        }
    }, [filteredProperties]);

    // Determinar si mostrar pestaña de imágenes (adjuntos existentes)
    const hasImages = attachments.length > 0;
    const allImages = [...attachments];

    // Detectar si es un polígono y obtener referencias OpenLayers
    const olFeature = featureProperties._olFeature;
    const olLayer = featureProperties._olLayer;
    const isPolygon = olFeature && (() => {
        const geometry = olFeature.getGeometry();
        if (!geometry) return false;
        const type = geometry.getType();
        return type === 'Polygon' || type === 'MultiPolygon';
    })();

    const handleEditGeometry = () => {
        if (!olFeature || !olLayer || !onStartGeometryEdit) return;

        if (onCloseModal) {
            onCloseModal();
        }

        setTimeout(() => {
            onStartGeometryEdit(olFeature, olLayer);
        }, 100);
    };

    return (
        <div className="card-sightings p-1 mt-0 pt-0">
            {(isPolygon || (isIncidence && canManage)) && (
                <div className="mb-3 d-flex justify-content-end gap-2">
                    {isPolygon && (!isIncidence || canManage || canCreate) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={handleEditGeometry}
                        >
                            <i className="bi bi-pencil-square me-1"></i>
                            {t("Editar geometría")}
                        </button>
                    )}
                    {isIncidence && canManage && (
                        <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => {
                                setShowReviewForm(true);
                                setActiveTab('reviews');
                            }}
                        >
                            <i className="bi bi-journal-plus me-1"></i>
                            {t("Añadir revisión")}
                        </button>
                    )}
                    {isIncidence && canManage && (
                        <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={handleDelete}
                            disabled={isDeleting}
                        >
                            <i className="bi bi-trash me-1"></i>
                            {isDeleting ? t("Eliminando...") : t("Eliminar")}
                        </button>
                    )}
                </div>
            )}
            <TabNavigation
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabs={[
                    { label: t("Atributos"), value: "details", icon: "bi-file-text-fill" },
                    ...(isIncidence && canManage ? [{ label: t("Revisiones"), value: "reviews", icon: "bi-journal-text" }] : []),
                    ...(hasImages ? [{ label: t("Imágenes"), value: "gallery", icon: "bi-images-fill" }] : [])
                ]}
            />
            <TabContent
                activeTab={activeTab}
                contents={[
                    {
                        value: "details",
                        component: (
                            <form className="p-2 pt-0">
                                {filteredProperties
                                    .filter(([key]) => key !== "files" && key !== "_olFeature" && key !== "_olLayer")
                                    .map(([key, value, fieldType]) => {
                                        if (typeof value === "boolean") {
                                            return (
                                                <div className="mb-1 row" key={key}>
                                                    <label htmlFor={key} className="col-sm-4 col-form-label">{t(key)}</label>
                                                    <div className="col-sm-8 d-flex align-items-center">
                                                        <input type="checkbox" id={key} checked={value} disabled />
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (key === 'comment') {
                                            return (
                                                <div className="mb-1" key={key}>
                                                    <label htmlFor={key} className="col-form-label">{t(key)}</label>
                                                    <textarea
                                                        className="form-control"
                                                        id={key}
                                                        value={value || ""}
                                                        rows={4}
                                                        readOnly
                                                        style={{ resize: 'none', backgroundColor: '#e9ecef' }}
                                                    />
                                                </div>
                                            );
                                        }

                                        if (fieldType === 'URL' && value) {
                                            return (
                                                <div className="mb-1 row" key={key}>
                                                    <label className="col-sm-4 col-form-label">{t(key)}</label>
                                                    <div className="col-sm-8 d-flex align-items-center">
                                                        <a href={value} target="_blank" rel="noopener noreferrer" title={value} className="d-flex align-items-center gap-1">
                                                            <i className="bi bi-box-arrow-up-right"></i>
                                                            {t(key)}
                                                        </a>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (fieldType === 'Imagen' && value) {
                                            return (
                                                <div className="mb-1 row" key={key}>
                                                    <label className="col-sm-4 col-form-label">{t(key)}</label>
                                                    <div className="col-sm-8">
                                                        <a href={value} target="_blank" rel="noopener noreferrer" title={t('Abrir imagen')}>
                                                            <img src={value} alt={key} style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} />
                                                        </a>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="mb-1 row" key={key}>
                                                <label htmlFor={key} className="col-sm-4 col-form-label">{t(key)}</label>
                                                <div className="col-sm-8">
                                                    <textarea
                                                        className="form-control"
                                                        id={key}
                                                        value={t(value) || ""}
                                                        rows={Math.max(1, Math.ceil(String(t(value) || "").length / 35))}
                                                        readOnly
                                                        style={{ resize: "none", overflow: "hidden" }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}

                            </form>
                        )
                    },
                    ...(isIncidence && canManage ? [{
                        value: "reviews",
                        component: (
                            <div>
                                {showReviewForm && (
                                    <ReviewForm
                                        incidenceId={incidenceId}
                                        currentStatus={featureProps.status}
                                        onReviewCreated={handleReviewCreated}
                                        onCancel={() => setShowReviewForm(false)}
                                    />
                                )}
                                <ReviewHistory key={reviewKey} incidenceId={incidenceId} />
                            </div>
                        )
                    }] : []),
                    {
                        value: "gallery",
                        component: <ImageGallery images={allImages} />
                    }
                ]}
            />
        </div>
    );
};

export default CardFeature;
